
import { getStorageData as originalGetStorageData, setStorageData as originalSetStorageData, STORAGE_KEYS } from '../storage';

// Memory storage fallback for environments where localStorage isn't available
const memoryStorage: Record<string, any> = {};

/**
 * Get data from storage with fallback mechanisms for older browsers
 */
export async function getStorageData<T>(key: string): Promise<T | null> {
  try {
    // Try to use the standard storage mechanism first
    const { getStorageData } = await import('../storage');
    return await getStorageData<T>(key);
  } catch (error) {
    console.error(`Error getting storage data for key ${key}:`, error);
    
    // First fallback: Try direct localStorage access
    try {
      const item = localStorage.getItem(key);
      if (item) {
        return JSON.parse(item) as T;
      }
    } catch (localStorageError) {
      console.warn('localStorage fallback failed:', localStorageError);
    }
    
    // Second fallback: Try memory storage
    try {
      return memoryStorage[key] as T || null;
    } catch (memoryError) {
      console.error('Memory storage fallback failed:', memoryError);
    }
    
    return null;
  }
}

/**
 * Set data in storage with fallback mechanisms for older browsers
 */
export async function setStorageData<T>(key: string, data: T | null): Promise<void> {
  try {
    // Try to use the standard storage mechanism first
    const { setStorageData } = await import('../storage');
    return await setStorageData<T>(key, data);
  } catch (error) {
    console.error(`Error setting storage data for key ${key}:`, error);
    
    // First fallback: Try direct localStorage access
    try {
      if (data === null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(data));
      }
      return;
    } catch (localStorageError) {
      console.warn('localStorage fallback failed:', localStorageError);
    }
    
    // Second fallback: Use memory storage
    try {
      if (data === null) {
        delete memoryStorage[key];
      } else {
        memoryStorage[key] = data;
      }
      return;
    } catch (memoryError) {
      console.error('Memory storage fallback failed:', memoryError);
      throw error; // Re-throw the original error if all fallbacks fail
    }
  }
}

/**
 * Clear all data in storage with fallback mechanisms
 */
export async function clearAllData(): Promise<void> {
  try {
    const studentIds = await originalGetStorageData<string[]>(STORAGE_KEYS.STUDENTS) || [];
    for (const id of studentIds) {
      await originalSetStorageData(`${STORAGE_KEYS.STUDENTS}:${id}`, null);
    }
    await originalSetStorageData(STORAGE_KEYS.STUDENTS, []);
  } catch (error) {
    console.error('Error clearing data using standard method:', error);
    
    // Fallback: Try direct localStorage approach
    try {
      // Get student IDs from localStorage directly
      const studentIdsString = localStorage.getItem(STORAGE_KEYS.STUDENTS);
      const studentIds = studentIdsString ? JSON.parse(studentIdsString) : [];
      
      // Remove individual student data
      for (const id of studentIds) {
        localStorage.removeItem(`${STORAGE_KEYS.STUDENTS}:${id}`);
      }
      
      // Clear the student ID list
      localStorage.setItem(STORAGE_KEYS.STUDENTS, '[]');
      
    } catch (localStorageError) {
      console.error('Error clearing data using localStorage fallback:', localStorageError);
      
      // Final fallback: Clear memory storage
      Object.keys(memoryStorage).forEach(key => {
        delete memoryStorage[key];
      });
    }
  }
}

/**
 * Checks if storage is available and working correctly
 * Returns a tuple with [isAvailable, storageType]
 */
export function checkStorageAvailability(): [boolean, 'localStorage' | 'memoryFallback' | 'none'] {
  // Check localStorage
  try {
    const testKey = '_storage_test';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return [true, 'localStorage'];
  } catch (e) {
    // Check if memory fallback is working
    try {
      memoryStorage._test = true;
      delete memoryStorage._test;
      return [true, 'memoryFallback'];
    } catch (memError) {
      return [false, 'none'];
    }
  }
}
